import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { HelpCircle, Send, Clock } from "lucide-react";

const ESEMPIO = [
  { g: 1, quando: "subito", cosa: "Acqua, vino, antipasti", perche: "Il tavolo ha qualcosa davanti entro due minuti." },
  { g: 2, quando: "quando finiscono gli antipasti", cosa: "Primi", perche: "La cucina li inizia solo quando mandi il giro: non si freddano." },
  { g: 3, quando: "dopo i primi", cosa: "Secondi e contorni", perche: "Esce tutto insieme, senza che nessuno aspetti." },
  { g: 4, quando: "alla fine", cosa: "Dolci e caffè", perche: "Il bar li vede solo ora e non li prepara in anticipo." },
];

/** «Cos'è un giro?»: la domanda che fanno tutti al primo giorno. Un esempio concreto, niente teoria. */
export const SpiegaGiri = ({ testo = "Cos'è un giro?", className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="spiega-giri-btn"
        className={`inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 underline decoration-dotted hover:text-slate-900 ${className}`}
      >
        <HelpCircle className="h-3.5 w-3.5" /> {testo}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="spiega-giri-dialog">
          <DialogTitle className="font-head text-xl font-bold">Il giro è «quando esce», non «cosa è»</DialogTitle>
          <p className="text-sm text-slate-600">
            Prendi la comanda tutta in una volta, come fa il cliente quando ordina.
            Poi decidi <span className="font-semibold text-slate-900">in che momento</span> ogni cosa deve arrivare
            in tavola: quello è il giro. La cucina vede solo il giro che hai mandato, quindi niente piatti
            pronti troppo presto e niente tavolo che aspetta.
          </p>

          <div className="space-y-2">
            {ESEMPIO.map((r) => (
              <div key={r.g} className="rounded-lg border border-slate-200 p-3" data-testid={`spiega-giro-${r.g}`}>
                <p className="flex items-center gap-2 text-sm font-bold">
                  <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] text-white">Giro {r.g}</span>
                  <span className="text-slate-900">{r.cosa}</span>
                </p>
                <p className="mt-1 flex items-start gap-1.5 text-[12px] text-slate-500">
                  <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Lo mandi {r.quando}. {r.perche}
                </p>
              </div>
            ))}
          </div>

          <div className="rounded-lg bg-amber-50 p-3 text-[12px] leading-relaxed text-amber-900">
            <p className="flex items-center gap-1.5 font-bold">
              <Send className="h-3.5 w-3.5" /> Tre cose da ricordare
            </p>
            <ul className="mt-1.5 space-y-1">
              <li>• Tutto quello che deve uscire insieme va nello stesso giro.</li>
              <li>• Finché non premi «Manda», il reparto lo vede ma non lo prepara.</li>
              <li>• Se cambi idea puoi spostare un piatto in un altro giro con un tocco sul suo badge.</li>
            </ul>
          </div>

          <Button className="h-11 w-full" onClick={() => setOpen(false)} data-testid="spiega-giri-close">
            Ho capito
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
